import { defineAsyncComponent, type Component } from 'vue';
import AddressArrayColumn from './AddressArrayColumn.vue';
import BucketGenreArrayColumn from './BucketGenreArrayColumn.vue';
import BucketTagArrayColumn from './BucketTagArrayColumn.vue';
import ContactArrayColumn from './ContactArrayColumn.vue';
import DefaultArrayContent from './DefaultArrayContent.vue';

// Buckets and custom lists share one drawer that draws each record as its
// search card. Loaded on first open, like the schedules drawer.
const ContainerCardArrayColumn = defineAsyncComponent(() => import('./ContainerCardArrayColumn.vue'));
import OwnerArrayColumn from './OwnerArrayColumn.vue';
import PhoneNumberArrayColumn from './PhoneNumberArrayColumn.vue';
import PlaylistUsagesArrayColumn from './PlaylistUsagesArrayColumn.vue';
import { scheduleBadges, scheduleItemSummaries } from './scheduleCell';
import ScheduleOneLinerList from '@/components/schedules/ScheduleOneLinerList.vue';
import { withPendingScheduleWrites } from '@/composables/api/scheduleItems';

/** One count badge in a cell that splits its count (see ArrayColumnRegistryEntry.badges). */
export interface ArrayColumnBadge {
  /** Stable key, rendered as `data-badge` for tests and styling hooks. */
  key: string;
  count: number;
  /** Drawn in place of the count, e.g. "—" for a count that has not been taken yet. */
  text?: string;
  /**
   * 'filled' is the house count badge; 'outlined' is the same colour drawn as a
   * hairline; 'problem' is a hairline in the destructive colour, for a count
   * that should be zero.
   */
  variant: 'filled' | 'outlined' | 'problem';
  /** Drawn even when the count is zero, so the badge keeps its place. */
  alwaysShow?: boolean;
  /** Optional mark drawn before the count. */
  icon?: Component;
  /** Tooltip and accessible label, e.g. "Scheduled by subscriber zones". */
  title?: string;
  /**
   * A whole phrase with the count in it, e.g. "2 schedules from subscriber
   * zones". Used as the tooltip and accessible label instead of "<count> <title>".
   */
  label?: string;
}

export interface ArrayColumnRegistryEntry {
  component: Component;
  title: string;
  /** Label shown on the badge when the array is empty (e.g. "Add" for owners). */
  emptyLabel?: string;
  /**
   * Opt-in: the lines behind the cell's joined text and its tooltip, computed
   * from the whole array and the parent row. Without it each item contributes
   * its `display_string || name`, unchanged.
   */
  itemSummaries?: (items: Record<string, unknown>[], row: Record<string, unknown>) => string[];
  /**
   * Opt-in: split the single count badge into several. Badges with a zero count
   * are hidden unless they set alwaysShow. Without it the cell shows one filled
   * count, unchanged.
   */
  badges?: (items: Record<string, unknown>[], row: Record<string, unknown>) => ArrayColumnBadge[];
  /**
   * Opt-in: the items the cell counts and summarises, given the raw array and
   * the parent row. The Schedules column applies this browser's pending
   * schedule writes, so a save shows before the reindexed document arrives.
   */
  items?: (items: Record<string, unknown>[], row: Record<string, unknown>) => Record<string, unknown>[];
  /**
   * Opt-in: a component drawn in the cell instead of the joined text, given the
   * parent row as `row`. The tooltip over it still lists every line from
   * itemSummaries. The Schedules column draws a one-line card for each schedule
   * the search card shows.
   */
  inline?: Component;
  /**
   * Opt-in: draw each item's name as a link that opens the item, instead of the
   * joined text. For short lists whose items people open, such as the zone
   * players a Unit serves.
   */
  linksItems?: boolean;
}

/**
 * Registry mapping class names to custom array column inspector content components.
 * Each entry provides the detail component rendered inside the inspector panel,
 * plus metadata used by ChildrenArrayColumn for the cell badge.
 */
export const arrayColumnRegistry: Record<string, ArrayColumnRegistryEntry> = {
  Address: { component: AddressArrayColumn, title: 'Addresses' },
  Bucket: { component: ContainerCardArrayColumn, title: 'Buckets' },
  BucketGenre: { component: BucketGenreArrayColumn, title: 'Bucket Genres' },
  Tag: { component: BucketTagArrayColumn, title: 'Tags' },
  Contact: { component: ContactArrayColumn, title: 'Contacts' },
  Contactable: { component: OwnerArrayColumn, title: 'Contact Owners', emptyLabel: 'Add' },
  PhoneNumber: { component: PhoneNumberArrayColumn, title: 'Phone Numbers' },
  PlaylistTrack: { component: PlaylistUsagesArrayColumn, title: 'Playlist Usages' },
  // A Unit's zone players. A Location Zone Player row from the API carries no
  // display_string, so each reads "Player 2007" by its id (see arrayItemName).
  LocationZonePlayer: { component: DefaultArrayContent, title: 'Players', linksItems: true },
  LocationZoneCustomPlaylist: {
    component: ContainerCardArrayColumn,
    title: 'Custom Lists',
  },
  LocationZoneCustomPlaylistSchedule: {
    component: defineAsyncComponent(() => import('./ScheduleArrayColumn.vue')),
    title: 'Schedules',
    itemSummaries: scheduleItemSummaries,
    badges: scheduleBadges,
    items: (items, row) => withPendingScheduleWrites(row.id, items) as Record<string, unknown>[],
    inline: ScheduleOneLinerList,
  },
};

/**
 * Get a custom array column registry entry for a given class type.
 * @param className - The class name (e.g., "BucketGenre")
 * @returns The registry entry if found, null otherwise
 */
export function getArrayColumnEntry(className: string): ArrayColumnRegistryEntry | null {
  return arrayColumnRegistry[className] || null;
}

